import { createQueryKeys } from '@lukemorales/query-key-factory';
import type { StoxlyClient } from '@stoxly/api-client';
import type { Body, Query } from '../op-types';

export function createInstrumentQueries(client: StoxlyClient) {
  return createQueryKeys('instruments', {
    // ['instruments', 'coverageReport', { query }]
    coverageReport: (query?: Query<'finnhubFundamentalsGetCoverageReport'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) =>
        client.finnhubFundamentalsGetCoverageReport({ queries: query, signal }),
    }),

    // ['instruments', 'streamQuotes', { query }]
    streamQuotes: (query: Query<'instrumentsStreamQuotes'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.instrumentsStreamQuotes({ queries: query, signal }),
    }),

    // ['instruments', 'searchUnified', { query }]
    searchUnified: (query: Query<'instrumentsSearchUnified'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.instrumentsSearchUnified({ queries: query, signal }),
    }),

    // ['instruments', 'marketStatus', { query }]
    marketStatus: (query?: Query<'stocksGetMarketStatus'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.stocksGetMarketStatus({ queries: query, signal }),
    }),

    // ['instruments', 'marketStatusBatch', { query }] — per-TICKER session
    // state for many symbols at once (the fox sidebar's session dots, F0).
    marketStatusBatch: (query: Query<'stocksGetBatchMarketStatus'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.stocksGetBatchMarketStatus({ queries: query, signal }),
    }),

    // ['instruments', 'popularToday', { query }] — most-picked search symbols
    // today (the fox sidebar's "Popularne dziś", F0).
    popularToday: (query?: Query<'instrumentsGetPopularToday'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.instrumentsGetPopularToday({ queries: query, signal }),
    }),

    // ['instruments', 'treasurySeries', code] — one Polish retail treasury bond series as the
    // issuer states it (`GET /treasury-bonds/series/:code`), keyed by the code alone: the
    // add-a-bond dialog asks it for the code it read out of the field, before any row exists.
    // Here rather than in a domain of its own: see the ceiling in `createDomainQueries`.
    treasurySeries: (code: string) => ({
      queryKey: [code],
      queryFn: ({ signal }) => client.treasurySeriesDescribe({ params: { code }, signal }),
    }),

    // ['instruments', 'fundSeries', { query }] — the catalog of funds whose TFI publishes a daily
    // unit price (`GET /fund-units/series`): the Fund kind of Add position searches it by name
    // before any row exists. Here for the same reason as `treasurySeries`.
    fundSeries: (query: Query<'fundUnitsSearchSeries'>) => ({
      queryKey: [{ query }],
      queryFn: ({ signal }) => client.fundUnitsSearchSeries({ queries: query, signal }),
    }),

    // ['instruments', 'fundSeriesVnav', seriesId, day] — the unit price a catalog fund had on a day
    // (the newest published on or before it): what a purchase of that day is prefilled with.
    fundSeriesVnav: (seriesId: number, day: string) => ({
      queryKey: [seriesId, day],
      queryFn: ({ signal }) =>
        client.fundUnitsSeriesVnav({ params: { seriesId }, queries: { day }, signal }),
    }),

    // ['instruments', 'detail', symbol, ...]
    detail: (symbol: string) => ({
      queryKey: [symbol],
      queryFn: ({ signal }) =>
        client.instrumentsGetInstrumentDetail({ params: { symbol }, signal }),
      contextQueries: {
        cryptoHistory: (query: Query<'cryptoGetCryptoHistory'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.cryptoGetCryptoHistory({ params: { symbol }, queries: query, signal }),
        }),
        cryptoQuote: {
          queryKey: null,
          queryFn: ({ signal }) => client.cryptoGetCryptoQuote({ params: { symbol }, signal }),
        },
        fundamentalMetrics: {
          queryKey: null,
          queryFn: ({ signal }) =>
            client.finnhubFundamentalsGetFundamentalMetrics({ params: { symbol }, signal }),
        },
        optionHistory: (query: Query<'optionsGetOptionHistory'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.optionsGetOptionHistory({ params: { symbol }, queries: query, signal }),
        }),
        dividends: {
          queryKey: null,
          queryFn: ({ signal }) => client.stocksGetDividends({ params: { symbol }, signal }),
        },
        dividendAnalysis: {
          queryKey: null,
          queryFn: ({ signal }) => client.stocksGetDividendAnalysis({ params: { symbol }, signal }),
        },
        historicalPrices: (query: Query<'stocksGetHistoricalPrices'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.stocksGetHistoricalPrices({ params: { symbol }, queries: query, signal }),
        }),
        stockQuote: {
          queryKey: null,
          queryFn: ({ signal }) => client.stocksGetQuote({ params: { symbol }, signal }),
        },
        // PAGED (fox F6b, decision 8): the merged archive reaches ~1000 rows for
        // a US mega-cap, so the window travels in the key — two offsets are two
        // cache entries, never one that answers for both.
        analystRatings: (query?: Query<'analystRatingsGetAnalystRatings'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.analystRatingsGetAnalystRatings({
              params: { symbol },
              queries: query,
              signal,
            }),
        }),
        completeFundamentals: (query: Query<'finnhubFundamentalsGetCompleteFundamentals'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.finnhubFundamentalsGetCompleteFundamentals({
              params: { symbol },
              queries: query,
              signal,
            }),
        }),
        financialForecasts: (query?: Query<'finnhubFundamentalsGetFinancialForecasts'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.finnhubFundamentalsGetFinancialForecasts({
              params: { symbol },
              queries: query,
              signal,
            }),
        }),
        periodBounds: (query?: Query<'finnhubFundamentalsGetPeriodBounds'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.finnhubFundamentalsGetPeriodBounds({
              params: { symbol },
              queries: query,
              signal,
            }),
        }),
        // The fox F3 metric catalog: list + category + latest value + history/
        // forecast availability (the Fundamentals metric library + copilot tool).
        metricCatalog: (query?: Query<'finnhubFundamentalsGetMetricCatalog'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.finnhubFundamentalsGetMetricCatalog({
              params: { symbol },
              queries: query,
              signal,
            }),
        }),
        overviewMetrics: {
          queryKey: null,
          queryFn: ({ signal }) =>
            client.finnhubFundamentalsGetOverviewMetrics({ params: { symbol }, signal }),
        },
        congressionalTrading: {
          queryKey: null,
          queryFn: ({ signal }) =>
            client.instrumentOwnershipGetCongressionalTrading({ params: { symbol }, signal }),
        },
        companyExecutives: {
          queryKey: null,
          queryFn: ({ signal }) =>
            client.instrumentOwnershipGetCompanyExecutives({ params: { symbol }, signal }),
        },
        fundOwnership: (query?: Query<'instrumentOwnershipGetFundOwnership'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.instrumentOwnershipGetFundOwnership({
              params: { symbol },
              queries: query,
              signal,
            }),
        }),
        // The paged/searchable holder table (fox F6). Every output-affecting knob —
        // view, search, sort, page — rides the key, so a new page or a new search
        // term is a distinct cache entry rather than a silent overwrite of page 1.
        holderPage: (query?: Query<'instrumentOwnershipGetHolderPage'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.instrumentOwnershipGetHolderPage({
              params: { symbol },
              queries: query,
              signal,
            }),
        }),
        insiderTransactions: (query?: Query<'instrumentOwnershipGetInsiderTransactions'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.instrumentOwnershipGetInsiderTransactions({
              params: { symbol },
              queries: query,
              signal,
            }),
        }),
        institutionalOwnership: (
          query?: Query<'instrumentOwnershipGetInstitutionalOwnership'>,
        ) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.instrumentOwnershipGetInstitutionalOwnership({
              params: { symbol },
              queries: query,
              signal,
            }),
        }),
        // The daily ownership-percentage series (fox F6b). The `months` window is
        // output-affecting, so it rides the key — a 36-month request must not
        // overwrite the 12-month entry the KPI chart is reading.
        ownershipHistory: (query?: Query<'ownershipHistoryGetOwnershipHistory'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.ownershipHistoryGetOwnershipHistory({
              params: { symbol },
              queries: query,
              signal,
            }),
        }),
        stockOwnership: (query?: Query<'instrumentOwnershipGetStockOwnership'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.instrumentOwnershipGetStockOwnership({
              params: { symbol },
              queries: query,
              signal,
            }),
        }),
        peers: {
          queryKey: null,
          queryFn: ({ signal }) => client.instrumentsGetPeers({ params: { symbol }, signal }),
        },
        profile: {
          queryKey: null,
          queryFn: ({ signal }) => client.instrumentsGetProfile({ params: { symbol }, signal }),
        },
        quote: (query: Query<'instrumentsGetQuote'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.instrumentsGetQuote({ params: { symbol }, queries: query, signal }),
        }),
        valuation: (query?: Query<'valuationGetValuation'>) => ({
          queryKey: [{ query }],
          queryFn: ({ signal }) =>
            client.valuationGetValuation({ params: { symbol }, queries: query, signal }),
        }),
      },
    }),
  });
}

export function createInstrumentMutations(client: StoxlyClient) {
  return {
    // Fox F0: report which symbol the user PICKED from unified-search results —
    // the telemetry behind the sidebar's "Popularne dziś" ranking. Fire-and-
    // forget on the FE (a failed report must never break navigation).
    trackSearchSelection: {
      mutationFn: (body: Body<'instrumentsTrackSearchSelection'>) =>
        client.instrumentsTrackSearchSelection(body),
    },
  };
}
